import { useState } from "react";
import { motion } from "framer-motion";
import {
  Gauge,
  Thermometer,
  Waves,
  Zap,
  Droplets,
  FlaskConical,
  CloudDrizzle,
  Ruler,
  Truck,
  MessageCircleQuestion,
  Maximize2,
} from "lucide-react";
import { SERVICES } from "@/data/content";
import InstrumentLightbox from "@/components/InstrumentLightbox";

const ICONS = {
  pressure: Gauge,
  temperature: Thermometer,
  flow: Waves,
  electrical: Zap,
  conductivity: Droplets,
  ph: FlaskConical,
  humidity: CloudDrizzle,
  level: Ruler,
  onsite: Truck,
  other: MessageCircleQuestion,
};

const FEATURED = SERVICES.filter((s) => s.image);
const REST = SERVICES.filter((s) => !s.image);

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5, delay, ease: "easeOut" },
});

export default function Services() {
  const [lightbox, setLightbox] = useState(null);
  const openPhoto = (service) =>
    setLightbox({
      name: service.name,
      tag: service.tag || `[ ${service.name.toUpperCase()} ]`,
      src: service.image || service.thumb,
      alt: service.imageAlt || service.thumbAlt,
      caption: service.imageAlt || service.thumbAlt,
    });

  return (
    <section id="services" data-testid="services-section" className="relative bg-base py-20 md:py-24">
      <InstrumentLightbox item={lightbox} onClose={() => setLightbox(null)} />
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div {...reveal()} className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="hud-label">[ 01 / SERVICES ]</p>
            <h2 className="mt-4 font-heading text-3xl font-semibold uppercase tracking-tight text-ink md:text-4xl lg:text-5xl">
              What we calibrate
            </h2>
          </div>
          <span aria-hidden="true" className="hud-label hidden lg:block">SYS.SCOPE // {SERVICES.length} DISCIPLINES</span>
        </motion.div>

        {/* featured disciplines — photo cards */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {FEATURED.map((service, i) => {
            const Icon = ICONS[service.key];
            return (
              <motion.article
                key={service.key}
                data-testid={`service-card-${service.key}`}
                {...reveal(i * 0.08)}
                className="corner-brackets card-surface group flex flex-col transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:border-brand/50 hover:shadow-[0_0_30px_rgb(var(--c-brand)/0.15)]"
              >
                <button
                  type="button"
                  onClick={() => openPhoto(service)}
                  aria-label={`View ${service.name} photo full size`}
                  data-testid={`service-image-${service.key}`}
                  className="relative block h-44 w-full cursor-zoom-in overflow-hidden border-b border-line/10 bg-[#0A0F1A] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <img
                    src={service.image}
                    alt={service.imageAlt}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-85 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/85 via-transparent to-transparent" />
                  <span className="hud-label photo-tile-label absolute bottom-3 left-5">{service.tag}</span>
                  <Maximize2 aria-hidden="true" className="absolute right-3 top-3 h-4 w-4 text-white/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </button>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                    <h3 className="font-heading text-xl font-medium tracking-tight text-ink">{service.name}</h3>
                  </div>
                  <p className="mt-2.5 text-sm leading-relaxed text-body">{service.blurb}</p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* remaining disciplines — compact rows */}
        <motion.ul {...reveal(0.1)} data-testid="services-compact-list" className="mt-5 grid grid-cols-1 gap-px border border-line/10 bg-line/10 sm:grid-cols-2 lg:grid-cols-3">
          {REST.map((service) => {
            const Icon = ICONS[service.key];
            return (
              <li
                key={service.key}
                data-testid={`service-card-${service.key}`}
                className="group flex items-center gap-4 bg-surface p-5 transition-colors duration-300 hover:bg-brand/5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-brand/25 bg-brand/10 transition-colors duration-300 group-hover:bg-brand/20">
                  <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-base font-medium tracking-tight text-ink">{service.name}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-body">{service.blurb}</p>
                </div>
                {service.thumb && (
                  <button
                    type="button"
                    onClick={() => openPhoto(service)}
                    aria-label={`View ${service.name} photo full size`}
                    data-testid={`service-thumb-${service.key}`}
                    className="shrink-0 cursor-zoom-in rounded-sm transition-transform duration-300 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    <img
                      src={service.thumb}
                      alt={service.thumbAlt}
                      loading="lazy"
                      style={service.thumbPosition ? { objectPosition: service.thumbPosition } : undefined}
                      className="h-14 w-14 rounded-sm border border-line/10 bg-white object-cover"
                    />
                  </button>
                )}
              </li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}
